import { runInAction, makeAutoObservable } from 'mobx';
import { INITIAL_SUBSCRIBER } from 'store/domain/electionStore';
import { RootStore } from 'store/rootStore';
import { EnrollmentPeriod } from 'types/enrollmentService';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { isEnrollmentPeriodNotEditable } from 'global/checklists-helpers/ChecklistHelper';
import { Subscriber, Choice, INITIAL_CHOICE, PersonInfo, ProductInfo } from 'types/electionService';
import {
  getSubscriberWithChoices,
  removeSubscriberDependent,
  addOrUpdateDependent,
  getProducts,
  updateSubscriber,
} from 'api/operations/electionService';
import { sortDependentsInSubscriber } from 'global/services-helpers/electionService';

export type WarningModalType = 'REMOVE_DEPENDENT' | 'FINISH_ENROLL | UPDATE_ENROLL';

// TODO refactor and transform to subscriber module.
export default class SubscriberStore {
  rootStore: RootStore;
  loading = false;
  currentSubscriber: Subscriber;
  periodId = '';

  isPeriodNotEditable = true;

  public get subscriberId(): string {
    return this.currentSubscriber.id;
  }

  private setLoading(loadingStatus: boolean): void {
    this.loading = loadingStatus;
  }

  private updatePeriodEditableStatus(status: boolean): void {
    this.isPeriodNotEditable = status;
  }

  public async addDependent(dependent: PersonInfo): Promise<void> {
    const res = await addOrUpdateDependent(this.currentSubscriber.id, dependent);
    // endpoint return only new dependent id
    const id = res.data?.addOrUpdateDependent;
    runInAction(() => {
      if (!id) return;
      this.currentSubscriber.custom.dependents.push({ ...dependent, id });
    });
  }

  public async editDependent(dependent: PersonInfo): Promise<void> {
    await addOrUpdateDependent(this.currentSubscriber.id, dependent);
    runInAction(() => {
      const updatedDependents = this.currentSubscriber.custom.dependents.map((dependentInfo): PersonInfo => {
        if (dependent.id === dependentInfo.id) return dependent;
        return { ...dependentInfo };
      });
      this.currentSubscriber.custom.dependents = updatedDependents;
    });
  }

  public removeDependent(dependentId: string): void {
    void removeSubscriberDependent(this.currentSubscriber.id, dependentId);

    // Remove dependent from general(custom) dependents list
    const updatedSubscriberDependents =
      this.currentSubscriber.custom.dependents.filter(({ id }) => dependentId !== id) || [];

    // Remove dependent from choice if subscriber removed it from dependents
    const updatedChoices = this.currentSubscriber.choice.map((choice) => {
      if (choice.dependentIds.includes(dependentId)) {
        const updatedDependentsList = choice.dependentIds.filter((id) => dependentId !== id);
        return { ...choice, dependentIds: updatedDependentsList };
      }
      return choice;
    });

    this.currentSubscriber = {
      ...this.currentSubscriber,
      choice: updatedChoices,
      custom: { dependents: updatedSubscriberDependents },
    };
  }

  async updatePrimary(periodId: string, answerValues: Record<string, string>): Promise<void> {
    this.currentSubscriber.common = { ...this.currentSubscriber.common, ...answerValues };
    await updateSubscriber(periodId, this.currentSubscriber);
  }

  public async setCurrentSubscriber(subscriberId: string): Promise<void> {
    this.setLoading(true);
    const subscriberInfo = await getSubscriberWithChoices(subscriberId, this.periodId);
    await runInAction(async () => {
      const sortedSubscriberInfo = sortDependentsInSubscriber(subscriberInfo);
      const products = await getProducts(this.periodId);

      // if there are no choices in subscriber make all dependents selected by default
      if (sortedSubscriberInfo.choice.length !== products?.length && products?.length) {
        sortedSubscriberInfo.choice = this.createInitialChoicesList(products);
      }
      this.currentSubscriber = sortedSubscriberInfo;

      this.setLoading(false);
    });
  }

  public resetCurrentSubscriber(): void {
    this.currentSubscriber = INITIAL_SUBSCRIBER;
  }

  private createInitialChoicesList(products: ProductInfo[]): Choice[] {
    const defaultDependentsList: string[] = this.currentSubscriber.custom.dependents.map(({ id }) => id) || [];

    const defaultInitialChoicesList: Choice[] =
      products.map(({ productLineId }) => {
        return { ...INITIAL_CHOICE, productLineId, dependentIds: defaultDependentsList };
      }) || [];

    return defaultInitialChoicesList;
  }

  // eslint-disable-next-line class-methods-use-this
  public getLinkToPortfolioDetails(
    currentSubscriber: Subscriber | null,
    productLineId: ProductLineId,
    products: ProductInfo[],
    cartId: string,
    periodId: string,
  ): string {
    const currentProductLinePortfolioId =
      products.find((product) => product.productLineId === productLineId)?.portfolioId || '';

    if (!currentProductLinePortfolioId) {
      throw new Error('Current product line portfolio id is not found');
    }

    return `/product-compare?type=available-products&portfolioIds=${currentProductLinePortfolioId}&cartId=${cartId}&productLineId=${productLineId}&periodId=${periodId}&eeClass=${
      currentSubscriber?.employeeClass || ''
    }`;
  }

  public setPeriodEditableStatus(enrollmentPeriod: EnrollmentPeriod): void {
    this.periodId = enrollmentPeriod.id;
    this.updatePeriodEditableStatus(isEnrollmentPeriodNotEditable(enrollmentPeriod.stage));
  }

  // eslint-disable-next-line class-methods-use-this
  public getCurrentChoice(subscriber: Subscriber, productLineId: string): Choice | undefined {
    const currentChoice = subscriber?.choice.find((choiceItem) => choiceItem.productLineId === productLineId);

    return currentChoice;
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.currentSubscriber = INITIAL_SUBSCRIBER;

    makeAutoObservable(this, {}, { autoBind: true });
  }
}
